import {
  Accordion,
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  AccordionToggleEventHandler,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
} from "@fluentui/react-components";
import {
  ArrowStepBackRegular,
  Dismiss24Regular,
  DocumentEditRegular,
  DocumentRegular,
  EyeTrackingOffRegular,
  EyeTrackingRegular,
} from "@fluentui/react-icons";
import { AppSpinner } from "components/AppSpinner/AppSpinner";
import { ButtonWithTooltip } from "components/ButtonWithTooltip/ButtonWithTooltip";
import { IPackageRowItem } from "components/PackagesView/components/BenefitImplementationPackages/shared";
import { Flex } from "components/styled";
import { useAppTheme } from "hooks/useAppTheme";
import { devices } from "library/constants";
import { fileExtensionIcons } from "library/shared";
import {
  Attachment,
  EAttachmentType,
  EmployeeRowItem,
  ETaxonomy,
  IClient,
  IEmployeeEnrollmentBase,
  IFormattedFile,
  IFormattedFileExtended,
  IRowItem,
} from "library/types";
import { getExtensionFromUrl, openLinkInNewTab } from "library/utils";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AddAttachments } from "./AddAttachments";
import { AttachmentItem } from "./AttachmentItem";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";

function isClient(
  item:
    | IRowItem
    | EmployeeRowItem
    | IPackageRowItem
    | IClient
    | IEmployeeEnrollmentBase
): item is IClient {
  return (item as IClient).clientName !== undefined;
}
function isEnrollment(
  item:
    | IRowItem
    | EmployeeRowItem
    | IPackageRowItem
    | IClient
    | IEmployeeEnrollmentBase
): item is IEmployeeEnrollmentBase {
  return (item as IEmployeeEnrollmentBase).employeeEnrollmentTag !== undefined;
}

type Props = {
  showIsAttachHiddenForEmployeeField?: boolean;
  item:
    | IRowItem
    | EmployeeRowItem
    | IPackageRowItem
    | IClient
    | IEmployeeEnrollmentBase;
  open: boolean;
  setOpen: (open: boolean) => void;
  attachmentActionsState: {
    createAttachmentInProgress: boolean;
    deleteInProgress: boolean;
    onCreateAttachment: (
      attach: IFormattedFile | null,
      isHiddenForEmployee?: boolean,
      newAttachList?: IFormattedFileExtended[],
      onSuccessFn?: () => void,
      attachmentType?: EAttachmentType
    ) => void;
    onDeleteAttachment: (attach: Attachment) => void;
    onUpdateAttachment: ({
      attachment,
      newValues,
    }: {
      attachment: Attachment;
      newValues: {
        attachmentName: string;
        attachmentSummary: string;
        attachmentAbstract: string;
        isHiddenForEmployee: boolean;
        attachmentType?: EAttachmentType;
      };
    }) => void;
    updateInProgress: boolean;
  };
  showAttachmentTypeSelection?: boolean;
  readOnly?: boolean;
};

const useStyles = makeStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    width: "650px",
  },
  content: {
    top: "100px",
    maxHeight: "80vh",

    scrollbarWidth: "thin",
  },
});

export function ManageAttachmentsDialog({
  item,
  open,
  setOpen,
  attachmentActionsState,
  showIsAttachHiddenForEmployeeField = true,
  showAttachmentTypeSelection,
  readOnly
}: Props) {
  const { t } = useTranslation();
  const theme = useAppTheme();

  const attachmentOptions = useTaxonomyOptions(ETaxonomy.AttachmentType);

  const [openAttachForms, setOpenAttachForms] = React.useState([""]);
  const handleToggleAttachForm: AccordionToggleEventHandler<string> = (
    event,
    data
  ) => {
    setOpenAttachForms(data.openItems);
  };

  const [attachmentList, setAttachmentList] = React.useState<Attachment[]>([]);

  const styles = useStyles();

  useEffect(() => {
    setAttachmentList(item.attachments || []);
  }, [item?.attachments]);

  const {
    deleteInProgress,
    createAttachmentInProgress,
    onCreateAttachment,
    updateInProgress,
    onDeleteAttachment,
    onUpdateAttachment,
  } = attachmentActionsState;

  const isManageClientAttachments = isClient(item);
  const isManageEnrollmentAttachments = isEnrollment(item);

  const subjectName = (() => {
    if (isManageClientAttachments) {
      return `${item?.clientName ?? item?.clientName}`;
    }

    if (isManageEnrollmentAttachments) {
      return `${item?.benefitPackageName}`;
    }
    return `${item?.name ?? `${item?.firstName} ${item.lastName}`}`;
  })();

  return (
    <>
      <Dialog
        modalType="alert"
        open={open}
        onOpenChange={(event, data) => setOpen(data.open)}
      >
        <StyledDialogSurface
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <DialogBody className={styles.content}>
            <DialogTitle
              action={
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<Dismiss24Regular />}
                  />
                </DialogTrigger>
              }
            >
              {subjectName}: {t("hbh.manageDocuments.label")}
            </DialogTitle>

            {deleteInProgress || createAttachmentInProgress ? (
              <AppSpinner />
            ) : null}
            <DialogContent className={styles.content}>
              <AttachmentList>
               {readOnly ? null : <AddAttachments
                  defaultAttachList={item.attachments || []}
                  showIsAttachHiddenForEmployeeField={
                    showIsAttachHiddenForEmployeeField
                  }
                  createAttachmentInProgress={createAttachmentInProgress}
                  onCreateAttachment={onCreateAttachment}
                  showAttachmentTypeSelection={showAttachmentTypeSelection}
                />}
                <Accordion
                  openItems={openAttachForms}
                  onToggle={handleToggleAttachForm}
                  multiple
                  collapsible
                >
                  {attachmentList?.map((attachment, index) => {
                    const docType = getExtensionFromUrl(
                      attachment.attachmentPath
                    );

                    const attachmentTypeLabel = attachmentOptions.find(
                      (option) => option.value === attachment.attachmentType
                    )?.text;

                    return (
                      <AccordionItem
                        key={index}
                        value={attachment?.attachmentName}
                      >
                        <AccordionHeader
                          size="large"
                          expandIconPosition="end"
                          expandIcon={
                            <Button
                              data-help="attachments-edit-toggle"
                              size="small"
                              appearance="subtle"
                              icon={
                                openAttachForms.includes(
                                  attachment?.attachmentName || ""
                                ) ? (
                                  <ArrowStepBackRegular />
                                ) : (
                                  <DocumentEditRegular />
                                )
                              }
                            />
                          }
                        >
                          <Flex justify="space-between" width="100%">
                            <ButtonWithTooltip
                              data-help="attachments-open"
                              tooltipContent={t(attachmentTypeLabel)}
                              style={{
                                color: theme.palette.purpleDark,
                                textDecoration: "underline",
                              }}
                              href={attachment?.attachmentPath || ""}
                              onClick={(e) => {
                                e.stopPropagation();
                                openLinkInNewTab(
                                  attachment?.attachmentPath || ""
                                );
                              }}
                              appearance="transparent"
                              icon={
                                fileExtensionIcons[docType] || (
                                  <DocumentRegular />
                                )
                              }
                            >
                              {attachment.attachmentName}
                            </ButtonWithTooltip>

                            {showIsAttachHiddenForEmployeeField && (
                              <ButtonWithTooltip
                                appearance="transparent"
                                tooltipContent={
                                  attachment.isHiddenForEmployee
                                    ? t("hbh.isHiddenForEmployee.label")
                                    : t("hbh.isVisibleForEmployee.label")
                                }
                                icon={
                                  attachment?.isHiddenForEmployee ? (
                                    <EyeTrackingOffRegular />
                                  ) : (
                                    <EyeTrackingRegular />
                                  )
                                }
                              />
                            )}
                          </Flex>
                        </AccordionHeader>
                        <AccordionPanel>
                          <AttachmentItem
                          readOnly={readOnly}
                            showIsAttachHiddenForEmployeeField={
                              showIsAttachHiddenForEmployeeField
                            }
                            deleteInProgress={deleteInProgress}
                            onDeleteAttachment={onDeleteAttachment}
                            attachment={attachment}
                            updateInProgress={updateInProgress}
                            onUpdateAttachment={onUpdateAttachment}
                            showAttachmentTypeSelection={
                              false //should be showAttachmentTypeSelection - but not implemented on BE yet
                            }
                            item={item}
                            key={attachment.attachmentName + "_" + index}
                          />
                        </AccordionPanel>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </AttachmentList>
            </DialogContent>
          </DialogBody>
        </StyledDialogSurface>
      </Dialog>
    </>
  );
}

const StyledDialogSurface = styled(DialogSurface)`
  margin-left: 10px;
  margin-right: 10px;
  width: 95%;
  min-width: 95%;
  max-height: 98vh;
  padding: 15px;

  @media only screen and ${devices.md} {
    width: 800px;
    min-width: 800px;
    padding: 24px;

    margin-left: auto;
    margin-right: auto;
  }
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
  //   border: 1px solid ${({ theme }) => theme.palette.neutralLight};
  margin: 10px 0;
`;

const AddNewAttachment = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid ${({ theme }) => theme.palette.neutralLight};
  padding: 5px;
  margin: 20px 0;
`;
